import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type AppModalSize = "sm" | "md" | "lg" | "xl";

const SIZE_CLASS: Record<AppModalSize, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-2xl",
};

export interface AppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;

  size?: AppModalSize;

  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;

  contentClassName?: string;
  bodyClassName?: string;

  /** 完全カスタムフッター。指定時はprimary/secondary関連propsは無視される */
  footer?: React.ReactNode;

  primaryLabel?: string;
  onPrimary?: () => void | Promise<void>;
  primaryLoading?: boolean;
  primaryDisabled?: boolean;
  primaryVariant?: "default" | "destructive";

  secondaryLabel?: string;
  onSecondary?: () => void;

  onClose?: () => void;
}

export function AppModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = "md",
  closeOnOutsideClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  contentClassName,
  bodyClassName,
  footer,
  primaryLabel,
  onPrimary,
  primaryLoading = false,
  primaryDisabled = false,
  primaryVariant = "default",
  secondaryLabel,
  onSecondary,
  onClose,
}: AppModalProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) onClose?.();
  };

  const renderFooter = () => {
    if (footer !== undefined) return footer;
    if (!primaryLabel && !secondaryLabel) return null;

    return (
      <DialogFooter>
        {secondaryLabel && (
          <Button
            type="button"
            variant="outline"
            onClick={onSecondary ?? (() => handleOpenChange(false))}
            disabled={primaryLoading}
          >
            {secondaryLabel}
          </Button>
        )}
        {primaryLabel && (
          <Button
            type="button"
            variant={primaryVariant}
            onClick={() => onPrimary?.()}
            disabled={primaryDisabled || primaryLoading}
          >
            {primaryLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {primaryLabel}
          </Button>
        )}
      </DialogFooter>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(SIZE_CLASS[size], contentClassName)}
        showCloseButton={showCloseButton}
        onPointerDownOutside={(event) => {
          if (!closeOnOutsideClick) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (!closeOnEscape) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {children && <div className={cn("text-sm", bodyClassName)}>{children}</div>}

        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
}
